import "@components/ui/text_input_with_tags.scss";

import type { ReactNode } from "react";
import classNames from "classnames";
import { TextInput } from "@sprout/text_input";
import { SecondaryButton } from "@sprout/buttons";
import { DestroyableTag } from "@sprout/tag";
import { useTagListInput } from "@hooks/use_tag_list_input";

type TagsProps = {
  values: string[];
  onRemove: (value: string) => void;
  quoted: boolean;
  truncate: boolean;
};

const Tags = ({ values, onRemove, quoted, truncate }: TagsProps) => {
  if (values.length === 0) {
    return null;
  }

  return (
    <div className="text-input-with-tags__tags">
      {values.map((value) => (
        <DestroyableTag
          key={value}
          label={quoted ? `"${value}"` : value}
          className="tag-item"
          size="md"
          truncate={truncate}
          maxWidth={truncate ? undefined : "max-w-full"}
          onClick={() => onRemove(value)}
        />
      ))}
    </div>
  );
};

type Props = {
  id: string;
  values: string[];
  onChange: (values: string[]) => void;
  max: number;
  maxMessage: string;
  addLabel: string;
  label?: string | null;
  placeholder?: string;
  helperText?: ReactNode;
  maxReachedText?: string;
  error?: string | null;
  maxLength?: number;
  icon?: ReactNode;
  trailing?: ReactNode;
  quoted?: boolean;
  short?: boolean;
  truncateTags?: boolean;
  validate?: (value: string, nextValues: string[]) => string | null;
  className?: string;
};

const TextInputWithTags = ({
  id,
  values,
  onChange,
  max,
  maxMessage,
  addLabel,
  label = null,
  placeholder,
  helperText,
  maxReachedText,
  error,
  maxLength = 255,
  icon,
  trailing,
  quoted = false,
  short = false,
  truncateTags = false,
  validate,
  className,
}: Props) => {
  const input = useTagListInput({ values, onChange, max, maxMessage, validate });
  const hasLabel = !!label;
  const maxExplainedInHelp = input.atLimit && !!maxReachedText;
  const draftError = maxExplainedInHelp && input.error === maxMessage ? null : input.error;
  const help = maxExplainedInHelp ? maxReachedText : helperText;

  return (
    <div className={classNames("text-input-with-tags", className)}>
      <div className="text-input-with-tags__control">
        <div
          className={classNames("text-input-with-tags__row", {
            "text-input-with-tags__row--labelled": hasLabel,
          })}
        >
          <div className="text-input-with-tags__field">
            <div className="text-input-with-tags__input">
              <TextInput
                id={id}
                label={label}
                placeholder={placeholder}
                value={input.draft}
                maxLength={maxLength}
                short={short}
                narrow={!hasLabel}
                icon={icon}
                error={draftError ?? error}
                onChange={(event) => input.changeDraft(event.currentTarget.value || "")}
                onKeyDown={input.onKeyDown}
              />
            </div>
            {trailing}
          </div>
          <div className="text-input-with-tags__button">
            <SecondaryButton
              onClick={input.add}
              disabled={
                (maxExplainedInHelp && input.atLimit) || !!input.error || input.isDraftEmpty
              }
              size="medium"
            >
              {addLabel}
            </SecondaryButton>
          </div>
        </div>
        {help && (
          <p
            className="helper-text"
            id={`${id}-help`}
            aria-live="polite"
            data-testid={`${id}-help`}
          >
            {help}
          </p>
        )}
      </div>
      <Tags values={values} onRemove={input.remove} quoted={quoted} truncate={truncateTags} />
    </div>
  );
};

export default TextInputWithTags;
